import { Button, ComboBox, IDropdownOption, Stack, Text } from '@fluentui/react';
import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { AccessTokens } from '../../../services/API/BaseService';
import { getResponseBusinessDocuments } from '../../../services/API/StatisticsService';
import { ExportDataPanel } from './ExportDataPanel';
import { TransferDirection, StateCounts, StatisticsPeriod, DirectionLabels } from '../types/BusiessDocumentTypes';

type CardProps = {
  accessTokens: AccessTokens;
};

type StateKey = 'sent' | 'sendFailed' | 'retrieved' | 'retrievedFailed';
const UNKNOWN_DIR = -1 as const;

const ALL_DIRS = [TransferDirection.EasxToEasx, TransferDirection.EasxToBvg, TransferDirection.BvgToBvg, TransferDirection.BvgToEasx, UNKNOWN_DIR] as const;

type AnyDirection = (typeof ALL_DIRS)[number];

const DIR_LABELS: Record<string, string> = {
  ...DirectionLabels,
  [UNKNOWN_DIR]: 'Unknown',
};

const STATE_BASE: Record<StateKey, string> = {
  sent: '#4CAF50',
  sendFailed: '#F44336',
  retrieved: '#2196F3',
  retrievedFailed: '#FF9800',
};

const DIR_SHADE_FACTORS: Record<string, number> = {
  [TransferDirection.EasxToEasx]: 0.62,
  [TransferDirection.EasxToBvg]: 0.42,
  [TransferDirection.BvgToBvg]: 0.22,
  [TransferDirection.BvgToEasx]: 0.08,
  [UNKNOWN_DIR]: 0.3,
};

type MixedBusinessDocPoint = {
  period: Date;

  sentTotal: number;
  sendFailedTotal: number;
  retrievedTotal: number;
  retrievedFailedTotal: number;

  [key: string]: any;
};

const keyOf = (state: StateKey, dir: AnyDirection) => `${state}_${dir}`;
const safeTotal = (s?: StateCounts) => s?.total ?? 0;

const normalizeStateCounts = (s?: StateCounts): Record<string, number> => {
  const total = safeTotal(s);
  const byDir = s?.byDirection ?? {};
  const normalized: Record<string, number> = {};

  let sumKnown = 0;
  for (const d of ALL_DIRS) {
    if (d === UNKNOWN_DIR) continue;
    const v = byDir[d as number] ?? 0;
    normalized[d as number] = v;
    sumKnown += v;
  }

  normalized[UNKNOWN_DIR] = Math.max(0, total - sumKnown) + (byDir[UNKNOWN_DIR] ?? 0);
  return normalized;
};

function generateBusinessDocumentMixed(date: Date, obj: any | undefined): MixedBusinessDocPoint {
  const sent = obj?.sent as StateCounts | undefined;
  const sendFailed = obj?.sendFailed as StateCounts | undefined;
  const retrieved = obj?.retrieved as StateCounts | undefined;
  const retrievedFailed = obj?.retrievedFailed as StateCounts | undefined;

  const sentNorm = normalizeStateCounts(sent);
  const sendFailedNorm = normalizeStateCounts(sendFailed);
  const retrievedNorm = normalizeStateCounts(retrieved);
  const retrievedFailedNorm = normalizeStateCounts(retrievedFailed);

  const point: MixedBusinessDocPoint = {
    period: date,
    sentTotal: safeTotal(sent),
    sendFailedTotal: safeTotal(sendFailed),
    retrievedTotal: safeTotal(retrieved),
    retrievedFailedTotal: safeTotal(retrievedFailed),
  };

  for (const dir of ALL_DIRS) {
    point[keyOf('sent', dir)] = sentNorm[dir] ?? 0;
    point[keyOf('sendFailed', dir)] = sendFailedNorm[dir] ?? 0;
    point[keyOf('retrieved', dir)] = retrievedNorm[dir] ?? 0;
    point[keyOf('retrievedFailed', dir)] = retrievedFailedNorm[dir] ?? 0;
  }

  return point;
}

const stateDirColor = (state: StateKey, dir: AnyDirection) => {
  const base = STATE_BASE[state];
  const factor = DIR_SHADE_FACTORS[dir] ?? 0.3;
  return tintTowardsWhite(base, factor);
};

function tintTowardsWhite(hex: string, factor: number) {
  const { r, g, b } = hexToRgb(hex);
  const mix = (c: number) => Math.round(c + (255 - c) * clamp01(factor));
  return rgbToHex(mix(r), mix(g), mix(b));
}

function clamp01(n: number) {
  return Math.max(0, Math.min(1, n));
}

function hexToRgb(hex: string) {
  const raw = hex.replace('#', '').trim();
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw;
  const num = parseInt(full, 16);
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  };
}

function rgbToHex(r: number, g: number, b: number) {
  const to = (n: number) => n.toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

export const BusinessDocumentCard: React.FC<CardProps> = ({ accessTokens }: CardProps) => {
  const defaultSelectedItem = { key: StatisticsPeriod.Hourly, text: 'Hourly' } as IDropdownOption;

  const [exportPopupState, setExportPopupState] = useState(false);
  const [businessDocuments, setBusinessDocuments] = useState<MixedBusinessDocPoint[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<IDropdownOption>(defaultSelectedItem);

  const periodOptions: IDropdownOption[] = useMemo(
    () => [
      defaultSelectedItem,
      { key: StatisticsPeriod.Daily, text: 'Daily' },
      { key: StatisticsPeriod.Monthly, text: 'Monthly' },
      { key: StatisticsPeriod.Yearly, text: 'Yearly' },
    ],
    [defaultSelectedItem],
  );

  useEffect(() => {
    getResponseBusinessDocuments(accessTokens, selectedPeriod.key as string).then((d: any[]) => {
      let stats: MixedBusinessDocPoint[] = [];
      const now = new Date();
      const date = new Date(now);

      switch (selectedPeriod.key) {
        case StatisticsPeriod.Hourly: {
          stats = Array.from({ length: 24 }).map((_, y) => {
            const hourDate = new Date(date);
            hourDate.setHours(now.getHours() - y);
            hourDate.setMinutes(0, 0, 0);

            const data = d.find((x) => {
              const st = moment(x.period).toDate();
              return st.getHours() === hourDate.getHours() && st.getDay() === hourDate.getDay();
            });

            return generateBusinessDocumentMixed(hourDate, data);
          });
          break;
        }
        case StatisticsPeriod.Daily: {
          stats = Array.from({ length: 30 }).map((_, y) => {
            const dayDate = new Date(date);
            dayDate.setDate(now.getDate() - y);
            dayDate.setHours(0, 0, 0, 0);

            const data = d.find((x) => {
              const st = moment(x.period).toDate();
              return st.getDate() === dayDate.getDate() && st.getMonth() === dayDate.getMonth();
            });

            return generateBusinessDocumentMixed(dayDate, data);
          });
          break;
        }
        case StatisticsPeriod.Monthly: {
          stats = Array.from({ length: 12 }).map((_, y) => {
            const monthDate = new Date(date);
            monthDate.setMonth(now.getMonth() - y);
            monthDate.setDate(1);
            monthDate.setHours(0, 0, 0, 0);

            const data = d.find((x) => {
              const st = moment(x.period).toDate();
              return st.getMonth() === monthDate.getMonth() && st.getFullYear() === monthDate.getFullYear();
            });

            return generateBusinessDocumentMixed(monthDate, data);
          });
          break;
        }
        case StatisticsPeriod.Yearly: {
          const YEARS = 5;

          stats = Array.from({ length: YEARS }).map((_, y) => {
            const yearDate = new Date(date);
            yearDate.setFullYear(now.getFullYear() - y);
            yearDate.setMonth(0, 1);
            yearDate.setHours(0, 0, 0, 0);

            const data = d.find((x) => {
              const st = moment(x.period).toDate();
              return st.getFullYear() === yearDate.getFullYear();
            });

            return generateBusinessDocumentMixed(yearDate, data);
          });

          break;
        }

        default:
          break;
      }

      stats.sort((a, b) => (a.period < b.period ? -1 : a.period > b.period ? 1 : 0));
      setBusinessDocuments(stats);
    });
  }, [accessTokens, selectedPeriod]);

  return (
    <Stack className="dashboardCardWrapper">
      <Stack horizontal horizontalAlign="start" tokens={{ childrenGap: 20 }}>
        <Text variant="large">Transfer document status</Text>

        <ComboBox
          selectedKey={selectedPeriod ? selectedPeriod.key : undefined}
          onChange={(_, item) => item && setSelectedPeriod(item)}
          placeholder="Period"
          options={periodOptions}
          multiSelect={false}
        />

        <Button type="button" iconProps={{ iconName: 'export' }} onClick={() => setExportPopupState(true)}>
          Export data
        </Button>

        {exportPopupState && (
          <ExportDataPanel period={selectedPeriod.key as number} isOpen={exportPopupState} onDismiss={() => setExportPopupState(false)} accessTokens={accessTokens} />
        )}
      </Stack>

      <Stack className="dashboardCardListWrapper">
        <ResponsiveContainer width="100%" height={290}>
          <BarChart width={500} height={290} data={businessDocuments} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />

            <XAxis dataKey="period" tickFormatter={(tick) => dateFormatter(tick, selectedPeriod.key as number)} fontSize={12} />

            <Tooltip content={<CustomTooltipMixed period={selectedPeriod.key} />} />
            <YAxis />

            <Legend content={<StateOnlyLegend />} />

            {ALL_DIRS.map((dir) => (
              <Bar key={`sent_${dir}`} dataKey={keyOf('sent', dir)} stackId="sent" fill={stateDirColor('sent', dir)} />
            ))}

            {ALL_DIRS.map((dir) => (
              <Bar key={`sendFailed_${dir}`} dataKey={keyOf('sendFailed', dir)} stackId="sendFailed" fill={stateDirColor('sendFailed', dir)} />
            ))}

            {ALL_DIRS.map((dir) => (
              <Bar key={`retrieved_${dir}`} dataKey={keyOf('retrieved', dir)} stackId="retrieved" fill={stateDirColor('retrieved', dir)} />
            ))}

            {ALL_DIRS.map((dir) => (
              <Bar key={`retrievedFailed_${dir}`} dataKey={keyOf('retrievedFailed', dir)} stackId="retrievedFailed" fill={stateDirColor('retrievedFailed', dir)} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Stack>
    </Stack>
  );
};

const dateFormatter = (item: any, period: number) => {
  const date = moment(item);

  switch (period) {
    case StatisticsPeriod.Hourly:
      return date.format('HH:mm');
    case StatisticsPeriod.Daily:
      return date.format('MMMM DD');
    case StatisticsPeriod.Monthly:
      return date.format('MMMM YYYY');
    case StatisticsPeriod.Yearly:
      return date.format('YYYY');
    default:
      return date.format('MMMM DD, HH:mm');
  }
};

const CustomTooltipMixed = ({ active, payload, label, period }: any) => {
  if (!active || !payload?.length) return null;

  const row: MixedBusinessDocPoint | undefined = payload?.[0]?.payload;

  const renderDirs = (state: StateKey, total: number) => {
    if (!row || total <= 0) return null;

    const lines = ALL_DIRS.map((dir) => ({ dir, v: row[keyOf(state, dir)] ?? 0 })).filter((x) => x.v > 0);
    if (!lines.length) {
      return <DirectionLine key={`${state}_${UNKNOWN_DIR}`} color={stateDirColor(state, UNKNOWN_DIR)} label={DIR_LABELS[UNKNOWN_DIR]} value={total} />;
    }

    console.log(lines);

    return lines.map(({ dir, v }) => <DirectionLine key={`${state}_${dir}`} color={stateDirColor(state, dir)} label={DIR_LABELS[dir] ?? dir} value={v} />);
  };

  const renderState = (state: StateKey, title: string, total: number) => (
    <Stack tokens={{ childrenGap: 2 }}>
      <Text styles={{ root: { fontWeight: 600 } }}>
        {title}: {total}
      </Text>
      {renderDirs(state, total)}
    </Stack>
  );

  return (
    <Stack className="chartTooltipWrapper" tokens={{ childrenGap: 8 }}>
      <Text>{dateFormatter(label, period)}</Text>

      {renderState('sent', 'Sent documents', row?.sentTotal ?? 0)}
      {renderState('sendFailed', 'Send failed', row?.sendFailedTotal ?? 0)}
      {renderState('retrieved', 'Retrieved documents', row?.retrievedTotal ?? 0)}
      {renderState('retrievedFailed', 'Retrieve failed', row?.retrievedFailedTotal ?? 0)}
    </Stack>
  );
};

const DirectionLine = ({ color, label, value }: { color: string; label: string; value: number }) => {
  return (
    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }}>
      <div
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: color,
          flexShrink: 0,
        }}
      />

      <Text
        styles={{
          root: {
            fontSize: 12,
            opacity: 0.9,
          },
        }}
      >
        {label}: {value}
      </Text>
    </Stack>
  );
};

const StateOnlyLegend = () => {
  return (
    <Stack horizontal horizontalAlign="center" tokens={{ childrenGap: 12 }}>
      <LegendItem color={STATE_BASE.sent} label="Sent documents" />
      <LegendItem color={STATE_BASE.sendFailed} label="Send failed" />
      <LegendItem color={STATE_BASE.retrieved} label="Retrieved documents" />
      <LegendItem color={STATE_BASE.retrievedFailed} label="Retrieve failed" />
    </Stack>
  );
};

const LegendItem = ({ color, label }: { color: string; label: string }) => (
  <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }}>
    <div style={{ width: 10, height: 10, backgroundColor: color }} />
    <Text>{label}</Text>
  </Stack>
);
